'use client'

import {
  createContext,
  useCallback,
  useContext,
  useSyncExternalStore
} from 'react'

type ScriptType = 'simplified' | 'traditional'

interface BlogScriptContextType {
  script: ScriptType
  setScript: (script: ScriptType) => void
  toggleScript: () => void
}

const BlogScriptContext = createContext<
  BlogScriptContextType['setScript'] | undefined
>(undefined)

const STORAGE_KEY = 'blog-script-preference'
const STORAGE_EVENT = 'blog-script-preference-change'
let memoryScriptPreference: ScriptType = 'simplified'

function loadScriptPreference(): ScriptType {
  if (typeof window === 'undefined') {
    return 'simplified'
  }
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'traditional' || stored === 'simplified') {
      return stored
    }
  } catch (error) {
    console.error('Failed to load blog script preference:', error)
  }
  return memoryScriptPreference
}

function saveScriptPreference(script: ScriptType) {
  memoryScriptPreference = script
  if (typeof window === 'undefined') {
    return
  }
  try {
    localStorage.setItem(STORAGE_KEY, script)
  } catch (error) {
    console.error('Failed to save blog script preference:', error)
  }
  window.dispatchEvent(new Event(STORAGE_EVENT))
}

function subscribeScriptPreference(onStoreChange: () => void) {
  const handleStorage = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY || event.key === null) onStoreChange()
  }
  window.addEventListener('storage', handleStorage)
  window.addEventListener(STORAGE_EVENT, onStoreChange)
  return () => {
    window.removeEventListener('storage', handleStorage)
    window.removeEventListener(STORAGE_EVENT, onStoreChange)
  }
}

export function BlogScriptProvider({
  children
}: {
  children: React.ReactNode
}) {
  return (
    <BlogScriptContext.Provider value={saveScriptPreference}>
      {children}
    </BlogScriptContext.Provider>
  )
}

export function useBlogScript() {
  const setScript = useContext(BlogScriptContext)
  // Each consumer uses the server snapshot for its own hydration. Updating
  // provider state first can reach a streamed child before it has hydrated.
  const script = useSyncExternalStore(
    subscribeScriptPreference,
    loadScriptPreference,
    () => 'simplified' as ScriptType
  )
  const toggleScript = useCallback(() => {
    setScript?.(script === 'simplified' ? 'traditional' : 'simplified')
  }, [script, setScript])
  if (setScript === undefined) {
    throw new Error('useBlogScript must be used within a BlogScriptProvider')
  }
  return { script, setScript, toggleScript }
}
